import { useState } from "react";
import Cropper from "react-easy-crop";
import { Monitor, Smartphone, X, ZoomIn } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

// Ratios d'affichage réels du hero d'accueil : section min-h-[540px] pleine largeur (desktop)
// et min-h-[420px] sur mobile (~390 px de large) — l'image est affichée en object-cover.
const ASPECTS = {
  desktop: { aspect: 32 / 9, maxWidth: 2560, label: "Desktop", icon: Monitor },
  mobile: { aspect: 13 / 14, maxWidth: 1200, label: "Mobile", icon: Smartphone },
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Cadrage centré par défaut couvrant la plus grande zone possible au ratio demandé
const defaultArea = (img, aspect) => {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  let cw = w;
  let ch = w / aspect;
  if (ch > h) {
    ch = h;
    cw = h * aspect;
  }
  return { x: Math.round((w - cw) / 2), y: Math.round((h - ch) / 2), width: Math.round(cw), height: Math.round(ch) };
};

const cropToBlob = async (src, area, maxWidth) => {
  const img = await loadImage(src);
  const scale = Math.min(1, maxWidth / area.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width * scale);
  canvas.height = Math.round(area.height * scale);
  canvas.getContext("2d").drawImage(img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
};

// Modale de recadrage de la bannière : un cadrage desktop et un cadrage mobile, appliqués ensemble.
export const HeroCropModal = ({ imageSrc, busy, onCancel, onApply }) => {
  const { t } = useLanguage();
  const [tab, setTab] = useState("desktop");
  const [states, setStates] = useState({
    desktop: { crop: { x: 0, y: 0 }, zoom: 1, area: null },
    mobile: { crop: { x: 0, y: 0 }, zoom: 1, area: null },
  });

  const cur = states[tab];
  const patchCur = (patch) => setStates((s) => ({ ...s, [tab]: { ...s[tab], ...patch } }));

  const apply = async () => {
    const img = await loadImage(imageSrc);
    const dArea = states.desktop.area || defaultArea(img, ASPECTS.desktop.aspect);
    const mArea = states.mobile.area || defaultArea(img, ASPECTS.mobile.aspect);
    const [desktopBlob, mobileBlob] = await Promise.all([
      cropToBlob(imageSrc, dArea, ASPECTS.desktop.maxWidth),
      cropToBlob(imageSrc, mArea, ASPECTS.mobile.maxWidth),
    ]);
    onApply({ desktopBlob, mobileBlob });
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/60 p-3 sm:p-4" data-testid="hero-crop-modal">
      <div className="w-full max-w-2xl rounded-3xl bg-white p-5 shadow-2xl sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-extrabold tracking-tight text-slate-900">{t("Recadrage de la bannière")}</h3>
            <p className="mt-1 text-xs text-slate-500">{t("Ajuste le cadrage pour le desktop et le mobile, puis applique.")}</p>
          </div>
          <button
            type="button"
            data-testid="hero-crop-close"
            onClick={onCancel}
            aria-label={t("Fermer")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 flex gap-1.5">
          {Object.entries(ASPECTS).map(([key, a]) => (
            <button
              key={key}
              type="button"
              data-testid={`hero-crop-tab-${key}`}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition ${
                tab === key
                  ? "border-[#2563EB] bg-[#2563EB] text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-[#2563EB]"
              }`}
            >
              <a.icon className="h-3.5 w-3.5" />
              {a.label}
            </button>
          ))}
        </div>

        <div className="relative mt-4 h-[260px] overflow-hidden rounded-2xl bg-slate-900 sm:h-[320px]" data-testid="hero-crop-area">
          <Cropper
            image={imageSrc}
            crop={cur.crop}
            zoom={cur.zoom}
            aspect={ASPECTS[tab].aspect}
            onCropChange={(crop) => patchCur({ crop })}
            onZoomChange={(zoom) => patchCur({ zoom })}
            onCropComplete={(_, areaPixels) => patchCur({ area: areaPixels })}
          />
        </div>

        <div className="mt-4 flex items-center gap-3">
          <ZoomIn className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="w-10 text-xs font-semibold text-slate-500">{t("Zoom")}</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={cur.zoom}
            data-testid="hero-crop-zoom"
            onChange={(e) => patchCur({ zoom: Number(e.target.value) })}
            className="flex-1 accent-[#2563EB]"
          />
        </div>

        <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
          <button
            type="button"
            data-testid="hero-crop-apply"
            onClick={apply}
            disabled={busy}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#2563EB] py-3 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] active:scale-[0.98] disabled:opacity-60"
          >
            {busy ? t("Publication…") : t("Appliquer le recadrage")}
          </button>
          <button
            type="button"
            data-testid="hero-crop-cancel"
            onClick={onCancel}
            className="rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB]"
          >
            {t("Annuler")}
          </button>
        </div>
      </div>
    </div>
  );
};
